(() => {
  const API_NAME = 'NordwegDeferredSectionRuntimeLoader';
  const CONFIG_SELECTOR = 'script[data-deferred-section-runtime-loader]';
  const INTENT_EVENTS = ['pointerdown', 'touchstart', 'focusin'];
  const GUARDED_COMPARE_TRIGGER_SELECTOR = 'button[data-nw-product-compare-trigger][disabled]';
  const REPLAYABLE_COMPARE_BUTTON_SELECTOR =
    'button[data-nw-product-compare-trigger], button[data-nw-product-compare-option]';
  const COMPARE_RUNTIME_PATH = /\/assets\/nw-product-compare\.js$/;
  const RUNTIME_PATH = /^\/cdn\/shop\/t\/\d+\/assets\/[a-z0-9][a-z0-9._-]*\.js$/i;

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const records = new Map();
  const pendingActivations = new WeakMap();
  const targetRecords = new WeakMap();
  const preparedTargets = new WeakMap();
  let observer = null;

  function trustedRuntimeUrl(source) {
    try {
      const url = new URL(source, window.location.href);
      if (url.protocol !== 'https:' || url.origin !== window.location.origin) return '';
      if (!RUNTIME_PATH.test(url.pathname)) return '';
      return url.href;
    } catch (_error) {
      return '';
    }
  }

  function recordFor(source) {
    const url = trustedRuntimeUrl(source);
    if (!url) return null;
    if (!records.has(url)) {
      records.set(url, {
        loaded: false,
        promise: null,
        targets: new Set(),
        url,
      });
    }
    return records.get(url);
  }

  function detachTarget(target, record) {
    INTENT_EVENTS.forEach((eventName) => target.removeEventListener(eventName, handleIntent));
    target.removeEventListener('click', handleActivation, true);
    observer?.unobserve(target);
    record.targets.delete(target);
    preparedTargets.delete(target);
  }

  function settleLoadedRecord(record) {
    record.targets.forEach((target) => detachTarget(target, record));
  }

  function load(source) {
    const record = recordFor(source);
    if (!record) return Promise.reject(new Error('A trusted deferred section runtime URL is unavailable.'));
    if (record.loaded) return Promise.resolve();
    if (record.promise) return record.promise;

    record.promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = record.url;
      script.async = true;
      script.dataset.deferredSectionRuntime = '';

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'error') {
          script.remove();
          reject(new Error(`Failed to load deferred section runtime: ${record.url}`));
          return;
        }
        record.loaded = true;
        resolve();
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).then(() => {
      settleLoadedRecord(record);
    }).catch((error) => {
      record.promise = null;
      throw error;
    });

    return record.promise;
  }

  function isLoaded(source) {
    return Boolean(recordFor(source)?.loaded);
  }

  function handleIntent(event) {
    const record = targetRecords.get(event.currentTarget || event.target);
    if (!record || record.loaded) return;
    load(record.url).catch(() => false);
  }

  function handleActivation(event) {
    const loaderTarget = event.currentTarget;
    const record = targetRecords.get(loaderTarget);
    if (!record || record.loaded || event.defaultPrevented) return;

    const activationTarget = event.target?.closest?.(REPLAYABLE_COMPARE_BUTTON_SELECTOR);
    if (!activationTarget || activationTarget.disabled || !loaderTarget.contains(activationTarget)) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const activation = { button: activationTarget, record };
    pendingActivations.set(loaderTarget, activation);
    load(record.url).then(() => {
      if (pendingActivations.get(loaderTarget) !== activation) return;
      pendingActivations.delete(loaderTarget);

      const compareElement = activation.button.closest('nw-product-compare');
      if (
        loaderTarget.isConnected === false ||
        activation.button.isConnected === false ||
        activation.button.disabled ||
        !loaderTarget.contains(activation.button) ||
        !compareElement ||
        !window.customElements?.get('nw-product-compare')
      ) return;

      activation.button.click();
    }).catch(() => {
      if (pendingActivations.get(loaderTarget) === activation) pendingActivations.delete(loaderTarget);
    });
  }

  function enableGuardedCompareTriggers(target, record) {
    if (!COMPARE_RUNTIME_PATH.test(new URL(record.url).pathname)) return;
    target.querySelectorAll?.(GUARDED_COMPARE_TRIGGER_SELECTOR).forEach((button) => {
      button.disabled = false;
    });
  }

  function prepareTarget(target, record) {
    if (!target || !record || record.loaded) return;
    const preparedRecord = preparedTargets.get(target);
    if (preparedRecord === record) return;
    if (preparedRecord) detachTarget(target, preparedRecord);

    targetRecords.set(target, record);
    preparedTargets.set(target, record);
    record.targets.add(target);
    INTENT_EVENTS.forEach((eventName) => target.addEventListener(eventName, handleIntent, { passive: true }));
    target.addEventListener('click', handleActivation, true);
    enableGuardedCompareTriggers(target, record);
  }

  function ensureObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const record = targetRecords.get(entry.target);
        if (record) load(record.url).catch(() => false);
      });
    }, { rootMargin: '1200px 0px', threshold: 0 });
    return observer;
  }

  function refresh() {
    const configurations = [...document.querySelectorAll(CONFIG_SELECTOR)];
    configurations.forEach((config) => {
      const target = document.getElementById(config.dataset.runtimeTargetId);
      const record = recordFor(config.dataset.runtimeScript);
      if (!target || !record || record.loaded) return;

      prepareTarget(target, record);
      if (config.dataset.designMode === 'true' || !('IntersectionObserver' in window)) {
        load(record.url).catch(() => false);
        return;
      }
      if (navigator.connection?.saveData) return;
      ensureObserver()?.observe(target);
    });
  }

  window[API_NAME] = { isLoaded, load, refresh };
  document.addEventListener('shopify:section:load', refresh);
  refresh();
})();
